import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiCheckCircle, FiXCircle, FiClock, FiInstagram, FiMessageSquare, FiTrash2, FiArrowRight, FiUsers, FiSearch, FiShare2 } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { SiTiktok } from 'react-icons/si';
import Swal from 'sweetalert2';
import { adminApi, getImageUrl } from '../../utils/api';
import { extractSocialUsername, buildSocialProfileUrl } from '../../utils/socialUsername';
import './AdminPages.css';

const AdminApplicationsPage = () => {
  const queryClient = useQueryClient();
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [campaignSearch, setCampaignSearch] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageData, setMessageData] = useState({ subject: '', content: '' });

  // Fetch campaigns for grid view
  const { data: campaignsData, isLoading: loadingCampaigns } = useQuery(
    'admin-campaigns-grid',
    () => adminApi.getCampaigns({ limit: 100 }),
    { enabled: !selectedCampaign }
  );

  // Fetch applications for selected campaign
  const { data: applicationsData, isLoading: loadingApplications } = useQuery(
    ['admin-applications', selectedCampaign?.id, page, statusFilter, searchTerm],
    () => adminApi.getApplications({
      page,
      limit: 50,
      status: statusFilter || undefined,
      campaignId: selectedCampaign?.id,
      search: searchTerm || undefined
    }),
    { enabled: !!selectedCampaign, keepPreviousData: true }
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const updateStatusMutation = useMutation(
    ({ id, status, notes }) => adminApi.updateApplicationStatus(id, status, notes),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-applications');
        queryClient.invalidateQueries('admin-stats');
        queryClient.invalidateQueries('user-stats');
        queryClient.invalidateQueries('admin-campaigns-grid');
        setSelectedApp(null);
        setAdminNotes('');
      }
    }
  );

  const sendMessageMutation = useMutation(
    (data) => adminApi.sendMessage(data),
    {
      onSuccess: () => {
        setShowMessageModal(false);
        setMessageData({ subject: '', content: '' });
        Swal.fire({
          title: 'נשלח!',
          text: 'ההודעה נשלחה בהצלחה',
          icon: 'success',
          confirmButtonColor: '#F7941D',
          confirmButtonText: 'אישור'
        });
      },
      onError: (error) => {
        console.error('Send message error:', error);
        Swal.fire({
          title: 'שגיאה!',
          text: error.response?.data?.error || 'שגיאה בשליחת ההודעה',
          icon: 'error',
          confirmButtonColor: '#F7941D',
          confirmButtonText: 'אישור'
        });
      }
    }
  );

  const deleteApplicationMutation = useMutation(
    (id) => adminApi.deleteApplication(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-applications');
        queryClient.invalidateQueries('admin-campaigns-grid');
        Swal.fire({
          title: 'נמחק!',
          text: 'ההגשה נמחקה בהצלחה',
          icon: 'success',
          confirmButtonColor: '#F7941D',
          confirmButtonText: 'אישור'
        });
      },
      onError: (error) => {
        console.error('Delete error:', error);
        Swal.fire({
          title: 'שגיאה!',
          text: error.response?.data?.error || 'שגיאה במחיקת ההגשה',
          icon: 'error',
          confirmButtonColor: '#F7941D',
          confirmButtonText: 'אישור'
        });
      }
    }
  );

  const handleDelete = (app) => {
    Swal.fire({
      title: 'למחוק את ההגשה?',
      text: `הגשה של ${app.display_name || app.user_name || app.full_name} לקמפיין ${selectedCampaign?.title}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'מחק',
      cancelButtonText: 'ביטול'
    }).then((result) => {
      if (result.isConfirmed) {
        deleteApplicationMutation.mutate(app.id);
      }
    });
  };

  const allCampaigns = campaignsData?.data?.campaigns || [];
  const campaigns = campaignSearch
    ? allCampaigns.filter(c => c.title?.toLowerCase().includes(campaignSearch.toLowerCase()))
    : allCampaigns;
  const applications = applicationsData?.data?.applications || [];
  const pagination = applicationsData?.data?.pagination || {};

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="badge badge-warning"><FiClock /> ממתין</span>;
      case 'approved':
        return <span className="badge badge-success"><FiCheckCircle /> אושר</span>;
      case 'rejected':
        return <span className="badge badge-error"><FiXCircle /> נדחה</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('he-IL');
  };

  const handleApprove = (app) => {
    setSelectedApp(app);
    setAdminNotes('');
  };

  const handleReject = (app) => {
    setSelectedApp(app);
    setAdminNotes('');
  };

  const confirmStatusChange = (status) => {
    if (!selectedApp) return;
    updateStatusMutation.mutate({
      id: selectedApp.id,
      status,
      notes: adminNotes
    });
  };

  const openMessageModal = (app) => {
    setSelectedApp(app);
    setMessageData({
      subject: `בנוגע להגשה שלך לקמפיין ${selectedCampaign?.title}`,
      content: ''
    });
    setShowMessageModal(true);
  };

  // Build a wa.me link with campaign details prefilled, opens WhatsApp Web / app directly
  const buildWhatsAppLink = (app) => {
    const rawPhone = app.user_phone || app.phone || '';
    const digits = String(rawPhone).replace(/\D/g, '');
    if (!digits) return null;
    // Israeli normalization: 0XXXXXXXXX -> 972XXXXXXXXX, leave 972 as-is
    let intl = digits;
    if (intl.startsWith('0')) intl = '972' + intl.slice(1);
    else if (!intl.startsWith('972') && intl.length === 9) intl = '972' + intl;

    const name = app.display_name || app.user_name || app.full_name || '';
    const campaignTitle = selectedCampaign?.title || '';
    const campaignUrl = selectedCampaign?.id
      ? `${window.location.origin}/campaigns/${selectedCampaign.id}`
      : '';

    const text = [
      `שלום ${name},`,
      ``,
      `אנחנו פונים אלייך בנוגע להגשה שלך לקמפיין "${campaignTitle}" ב-Weon.`,
      campaignUrl ? `קישור לקמפיין: ${campaignUrl}` : '',
      ``,
      `נשמח לתאם המשך - איזה שעה נוחה לך?`
    ].filter(Boolean).join('\n');

    return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
  };

  const sendMessage = () => {
    if (!selectedApp || !messageData.content) return;
    sendMessageMutation.mutate({
      receiverId: selectedApp.user_id,
      campaignId: selectedCampaign?.id,
      subject: messageData.subject,
      content: messageData.content
    });
  };

  const handleShareCampaign = (e, campaign) => {
    e.stopPropagation(); // Prevent card click
    const shareUrl = `${window.location.origin}/campaigns/${campaign.id}`;
    const shareText = `🎯 הזדמנות שיתוף פעולה!\n\n${campaign.title}\n\n👉 הצטרפו עכשיו:`;

    Swal.fire({
      title: 'שיתוף הקמפיין',
      html: `
        <div style="text-align: right; direction: rtl;">
          <p style="margin-bottom: 15px; color: #666;">שתפו את הקמפיין עם משפיענים</p>
          <div style="background: #f5f5f5; padding: 15px; border-radius: 10px; margin-bottom: 15px;">
            <p style="font-weight: bold; margin-bottom: 10px;">${campaign.title}</p>
          </div>
          <input type="text" id="share-url" value="${shareUrl}" readonly
            style="width: 100%; padding: 12px; border: 2px solid #F7941D; border-radius: 8px; text-align: left; direction: ltr; font-size: 14px; background: #fff8f0;" />
        </div>
      `,
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: '📱 קישור לפלטפורמות',
      denyButtonText: '🔗 קישור ישיר לצפיה',
      cancelButtonText: 'סגור',
      confirmButtonColor: '#F7941D',
      denyButtonColor: '#5c6bc0',
      cancelButtonColor: '#666',
    }).then((result) => {
      if (result.isConfirmed) {
        // Copy full message for platforms
        const fullShareText = `${shareText}\n${shareUrl}`;
        navigator.clipboard.writeText(fullShareText).then(() => {
          Swal.fire({
            icon: 'success',
            title: 'הקישור הועתק!',
            text: 'ההודעה המלאה הועתקה - מושלם לשיתוף ברשתות',
            confirmButtonColor: '#F7941D',
            timer: 2500,
            showConfirmButton: false
          });
        });
      } else if (result.isDenied) {
        // Copy direct URL only
        navigator.clipboard.writeText(shareUrl).then(() => {
          Swal.fire({
            icon: 'success',
            title: 'הקישור הועתק!',
            text: 'הקישור הישיר לצפייה בקמפיין הועתק',
            confirmButtonColor: '#F7941D',
            timer: 2000,
            showConfirmButton: false
          });
        });
      }
    });
  };

  // Campaign Grid View
  if (!selectedCampaign) {
    return (
      <div className="admin-page">
        <header className="admin-header">
          <div className="header-content">
            <h1>מועמדות משפיענים</h1>
            <span className="total-count">{campaigns.length} קמפיינים</span>
          </div>
        </header>

        {/* Campaign Search */}
        <div className="admin-toolbar">
          <div className="search-form">
            <div className="search-input-wrapper">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="חיפוש קמפיין..."
                value={campaignSearch}
                onChange={(e) => setCampaignSearch(e.target.value)}
              />
            </div>
            {campaignSearch && (
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setCampaignSearch('')}
              >
                נקה
              </button>
            )}
          </div>
        </div>

        {loadingCampaigns ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>טוען קמפיינים...</p>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="empty-state">
            <FiUser className="empty-icon" />
            <p>לא נמצאו קמפיינים</p>
          </div>
        ) : (
          <div className="campaigns-grid">
            {campaigns.map((campaign) => (
              <motion.div
                key={campaign.id}
                className="campaign-card-grid"
                onClick={() => setSelectedCampaign(campaign)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="campaign-card-image">
                  {(campaign.coverImage || campaign.cover_image_url) ? (
                    <img
                      src={getImageUrl(campaign.coverImage || campaign.cover_image_url)}
                      alt={campaign.title}
                    />
                  ) : (
                    <div className="placeholder-image">
                      <FiUsers />
                    </div>
                  )}
                </div>
                <div className="campaign-card-info">
                  <h3>{campaign.title}</h3>
                  <div className="campaign-card-footer">
                    <span className="applications-count">
                      <FiUsers /> {campaign.applicationCount || 0} {(campaign.applicationCount || 0) === 1 ? 'מועמד' : 'מועמדים'}
                    </span>
                    <button
                      className="btn-share-campaign"
                      onClick={(e) => handleShareCampaign(e, campaign)}
                      title="שיתוף קמפיין"
                    >
                      <FiShare2 />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Applications Table View for Selected Campaign
  return (
    <div className="admin-page">
      <button className="back-btn" onClick={() => { setSelectedCampaign(null); setStatusFilter(''); setSearchTerm(''); setSearchInput(''); setPage(1); }}>
        <FiArrowRight />
        חזרה לקמפיינים
      </button>

      <header className="admin-header">
        <div className="header-content">
          <h1>{selectedCampaign.title}</h1>
          <span className="total-count">{pagination.total || 0} {(pagination.total || 0) === 1 ? 'מועמד' : 'מועמדים'}</span>
        </div>
      </header>

      {/* Filters and Search */}
      <div className="admin-toolbar">
        <form className="search-form" onSubmit={handleSearch}>
          <div className="search-input-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="חיפוש משפיען לפי שם, אימייל או שם משתמש..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary btn-sm">
            חפש
          </button>
          {searchTerm && (
            <button type="button" className="btn btn-outline btn-sm" onClick={clearSearch}>
              נקה
            </button>
          )}
        </form>
        <div className="status-filters">
          <button
            className={`filter-chip ${statusFilter === '' ? 'active' : ''}`}
            onClick={() => { setStatusFilter(''); setPage(1); }}
          >
            הכל
          </button>
          <button
            className={`filter-chip pending ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => { setStatusFilter('pending'); setPage(1); }}
          >
            <FiClock /> ממתין
          </button>
          <button
            className={`filter-chip approved ${statusFilter === 'approved' ? 'active' : ''}`}
            onClick={() => { setStatusFilter('approved'); setPage(1); }}
          >
            <FiCheckCircle /> אושר
          </button>
          <button
            className={`filter-chip rejected ${statusFilter === 'rejected' ? 'active' : ''}`}
            onClick={() => { setStatusFilter('rejected'); setPage(1); }}
          >
            <FiXCircle /> נדחה
          </button>
        </div>
      </div>

      {/* Table */}
      {loadingApplications ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>טוען נתונים...</p>
        </div>
      ) : applications.length === 0 ? (
        <div className="empty-state">
          <FiUser className="empty-icon" />
          <p>לא נמצאו מועמדים לקמפיין זה</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>שם משפיען</th>
                <th>אימייל</th>
                <th>טלפון</th>
                <th>עיר</th>
                <th>סטטוס</th>
                <th>אינסטגרם</th>
                <th>טיקטוק</th>
                <th>תאריך הגשה</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.id}>
                  <td data-label="שם משפיען">
                    <span className="user-name-cell">
                      {app.display_name || app.user_name || app.full_name || '-'}
                    </span>
                  </td>
                  <td data-label="אימייל">{app.user_email || app.email || '-'}</td>
                  <td data-label="טלפון">
                    {app.user_phone ? (
                      <a href={`tel:${app.user_phone}`} className="phone-link">
                        {app.user_phone}
                      </a>
                    ) : '-'}
                  </td>
                  <td data-label="עיר">{app.user_city || '-'}</td>
                  <td data-label="סטטוס">{getStatusBadge(app.status)}</td>
                  <td data-label="אינסטגרם">
                    {(() => {
                      const ig = extractSocialUsername('instagram', app.instagram_username);
                      if (!ig) return '-';
                      return (
                        <a
                          href={buildSocialProfileUrl('instagram', ig)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-link instagram"
                        >
                          <FiInstagram /> @{ig}
                        </a>
                      );
                    })()}
                  </td>
                  <td data-label="טיקטוק">
                    {(() => {
                      const tt = extractSocialUsername('tiktok', app.tiktok_username);
                      if (!tt) return '-';
                      return (
                        <a
                          href={buildSocialProfileUrl('tiktok', tt)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-link tiktok"
                        >
                          <SiTiktok /> @{tt}
                        </a>
                      );
                    })()}
                  </td>
                  <td data-label="תאריך הגשה">{formatDate(app.created_at)}</td>
                  <td data-label="פעולות">
                    <div className="actions-cell">
                      {app.status === 'pending' && (
                        <>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleApprove(app)}
                            title="אשר"
                          >
                            <FiCheckCircle />
                          </button>
                          <button
                            className="btn btn-sm btn-error"
                            onClick={() => handleReject(app)}
                            title="דחה"
                          >
                            <FiXCircle />
                          </button>
                        </>
                      )}
                      {app.status === 'approved' && (
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => {
                            setSelectedApp(app);
                            setAdminNotes('');
                          }}
                          title="החזר לממתין"
                          style={{ background: '#f0ad4e', color: '#fff', border: 'none' }}
                        >
                          <FiClock />
                        </button>
                      )}
                      {app.status === 'rejected' && (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleApprove(app)}
                          title="אשר"
                        >
                          <FiCheckCircle />
                        </button>
                      )}
                      {(() => {
                        const waLink = buildWhatsAppLink(app);
                        return waLink ? (
                          <a
                            className="btn btn-sm btn-whatsapp"
                            href={waLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="פתח שיחה בוואטסאפ עם פרטי הקמפיין"
                          >
                            <FaWhatsapp />
                          </a>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline"
                            disabled
                            title="אין מספר טלפון למשפיען זה"
                          >
                            <FaWhatsapp />
                          </button>
                        );
                      })()}
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => openMessageModal(app)}
                        title="שלח הודעה במערכת"
                      >
                        <FiMessageSquare />
                      </button>
                      <button
                        className="btn btn-sm btn-error"
                        onClick={() => handleDelete(app)}
                        title="מחק"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            הקודם
          </button>
          <span className="page-info">
            עמוד {page} מתוך {pagination.pages}
          </span>
          <button
            className="btn btn-outline"
            disabled={page === pagination.pages}
            onClick={() => setPage(page + 1)}
          >
            הבא
          </button>
        </div>
      )}

      {/* Status Change Modal */}
      <AnimatePresence>
        {selectedApp && !showMessageModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedApp(null)}
          >
            <motion.div
              className="modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2>עדכון סטטוס הגשה</h2>
              <p>
                משפיען: <strong>{selectedApp.display_name || selectedApp.user_name || selectedApp.full_name}</strong>
              </p>
              <p>
                קמפיין: <strong>{selectedCampaign?.title}</strong>
              </p>

              <div className="form-group">
                <label>הערות מנהל (אופציונלי)</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="הוסף הערות..."
                  rows={3}
                />
              </div>

              <div className="modal-actions">
                {selectedApp?.status !== 'approved' && (
                  <button
                    className="btn btn-success"
                    onClick={() => confirmStatusChange('approved')}
                    disabled={updateStatusMutation.isLoading}
                  >
                    <FiCheckCircle /> אשר
                  </button>
                )}
                {selectedApp?.status !== 'rejected' && selectedApp?.status !== 'pending' && (
                  <button
                    className="btn btn-warning"
                    onClick={() => confirmStatusChange('pending')}
                    disabled={updateStatusMutation.isLoading}
                    style={{ background: '#f0ad4e', color: '#fff', border: 'none' }}
                  >
                    <FiClock /> החזר לממתין
                  </button>
                )}
                {selectedApp?.status !== 'rejected' && (
                  <button
                    className="btn btn-error"
                    onClick={() => confirmStatusChange('rejected')}
                    disabled={updateStatusMutation.isLoading}
                  >
                    <FiXCircle /> דחה
                  </button>
                )}
                <button
                  className="btn btn-outline"
                  onClick={() => setSelectedApp(null)}
                >
                  ביטול
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Message Modal */}
      <AnimatePresence>
        {showMessageModal && selectedApp && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowMessageModal(false)}
          >
            <motion.div
              className="modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2>שלח הודעה</h2>
              <p>
                אל: <strong>{selectedApp.display_name || selectedApp.user_name || selectedApp.full_name}</strong>
              </p>

              <div className="form-group">
                <label>נושא</label>
                <input
                  type="text"
                  value={messageData.subject}
                  onChange={(e) => setMessageData({ ...messageData, subject: e.target.value })}
                  placeholder="נושא ההודעה"
                />
              </div>

              <div className="form-group">
                <label>תוכן ההודעה</label>
                <textarea
                  value={messageData.content}
                  onChange={(e) => setMessageData({ ...messageData, content: e.target.value })}
                  placeholder="כתוב את ההודעה..."
                  rows={5}
                />
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-primary"
                  onClick={sendMessage}
                  disabled={!messageData.content || sendMessageMutation.isLoading}
                >
                  <FiMessageSquare /> שלח
                </button>
                <button
                  className="btn btn-outline"
                  onClick={() => setShowMessageModal(false)}
                >
                  ביטול
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminApplicationsPage;
